// ═══════════════════════════════════════════════════════════════════
//  OVERRIDE OVERLAY (AB#803)
//
//  The pure half of the dated week's override markers: which cells an
//  override changed, what to call it when somebody hovers or focuses
//  one, and which kinds the legend has to explain. Built from the week
//  endpoint's own answer (AB#802) through weekViewFrom; nothing here
//  renders or fetches (overrideOverlay.test.ts).
//
//  Only CMS's own overrides are ever here: the endpoint composes the
//  week from CMS's records, so an override made by hand in Cal is not
//  (decision ams-cml), and the legend says so.
// ═══════════════════════════════════════════════════════════════════

import type { ScheduleOverrideKindValue, SlotPlanWeekOverrideDto } from "../availabilityApi"
import type { EventTypeSlug } from "../../schedule-overrides/scheduleOverridesApi"
import { EVENT_TYPE_LABELS, formatWindow } from "../../schedule-overrides/lib/overrideForm"
import type { WeekView } from "./weekModel"

/** AmsScheduleOverrideKind, by name. */
export type OverrideKindKey = "siteClosure" | "eventTypeOverride" | "outOfOffice" | "outreachAssignment"

/** The legend's order: the whole site first, then one host. */
export const OVERRIDE_KIND_ORDER: readonly OverrideKindKey[] = [
  "siteClosure",
  "eventTypeOverride",
  "outOfOffice",
  "outreachAssignment",
]

/**
 * What a kind is called on the marker, in the tooltip and in the legend. The outreach assignment is
 * named for what it does (a host sent to another site), so a later rename of the kind changes only
 * this line.
 */
export const OVERRIDE_KIND_LABEL: Record<OverrideKindKey, string> = {
  siteClosure: "Site closure",
  eventTypeOverride: "Event type override",
  outOfOffice: "Unavailable / out of office",
  outreachAssignment: "Temporary site reassignment",
}

/** The legend's help text (decision ams-cml). */
export const OVERRIDE_LEGEND_HELP = "Marked slots were changed by a CMS schedule override. Overrides made directly in Cal are not shown."

const KIND_BY_NUMBER: readonly OverrideKindKey[] = ["outreachAssignment", "outOfOffice", "siteClosure", "eventTypeOverride"]

/**
 * An AmsScheduleOverrideKind as sent (0 outreach assignment, 1 out of office, 2 site closure,
 * 3 event-type override), or its name should a string converter ever be added; null when unreadable.
 */
export function decodeOverrideKind(value: ScheduleOverrideKindValue | null | undefined): OverrideKindKey | null {
  if (typeof value === "number") return KIND_BY_NUMBER[value] ?? null
  if (typeof value !== "string") return null
  if (/^\d$/.test(value)) return decodeOverrideKind(Number(value))
  const lowered = value.toLowerCase()
  return KIND_BY_NUMBER.find(kind => kind.toLowerCase() === lowered) ?? null
}

/** "2026-10-20T13:00:00-07:00" → ["2026-10-20", "13:00"]: the Pacific wall clock the server wrote. */
function wallClock(instant: string): [string, string] | null {
  const match = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/.exec(instant ?? "")
  return match ? [match[1]!, match[2]!] : null
}

/** "Tue, Oct 20, 1:00 PM – 3:00 PM", on the office's clock; the raw text when it cannot be read. */
export function overrideWindow(override: Pick<SlotPlanWeekOverrideDto, "start" | "end">): string {
  const start = wallClock(override.start)
  const end = wallClock(override.end)
  if (!start || !end) return `${override.start} – ${override.end}`
  return formatWindow(start[0], start[1], end[0], end[1])
}

/** Where or what it is about: "Kent", "Intake only at Kent", "every site". */
export function overrideTarget(override: SlotPlanWeekOverrideDto): string {
  const site = override.targetSiteName || override.targetTeamSlug || null
  const eventType = override.eventTypeSlug
    ? EVENT_TYPE_LABELS[override.eventTypeSlug as EventTypeSlug] ?? override.eventTypeSlug
    : null
  if (eventType) return site ? `${eventType} only at ${site}` : `${eventType} only, every site`
  return site ?? "every site"
}

/** The kind's label, or the server's own when the kind cannot be read. */
export function overrideKindLabel(override: SlotPlanWeekOverrideDto): string {
  const kind = decodeOverrideKind(override.kind)
  return kind ? OVERRIDE_KIND_LABEL[kind] : override.label || "Schedule override"
}

/** One override in a line, kind, Pacific window and target: "Site closure · Tue, Oct 20, 12:00 AM – 11:59 PM · Kent". */
export function describeWeekOverride(override: SlotPlanWeekOverrideDto): string {
  return `${overrideKindLabel(override)} · ${overrideWindow(override)} · ${overrideTarget(override)}`
}

/** A changed cell's marker. */
export interface CellMarker {
  /** The overrides that changed it, in the endpoint's order; the first is the one a click opens. */
  overrides: SlotPlanWeekOverrideDto[]
  /** Their kinds, once each, in legend order: one icon each. */
  kinds: OverrideKindKey[]
  /** What hovering or focusing it says: every override, one line each. */
  description: string
}

export interface OverrideOverlay {
  /** By cellKey; only cells an override (that the week names) changed are present. */
  markers: Record<string, CellMarker>
  /** One legend entry per kind of override in the week, in legend order. */
  legendKinds: OverrideKindKey[]
}

function kindsOf(overrides: readonly SlotPlanWeekOverrideDto[]): OverrideKindKey[] {
  const present = new Set(overrides.map(o => decodeOverrideKind(o.kind)).filter((k): k is OverrideKindKey => k !== null))
  return OVERRIDE_KIND_ORDER.filter(kind => present.has(kind))
}

/**
 * The markers over a drawn week. A cell whose ids name no override the week summarised (one the
 * caller may not see) stays unmarked: there would be nothing to name or open.
 */
export function overrideOverlay(view: Pick<WeekView, "overrideIdsByCell" | "overrides">): OverrideOverlay {
  const byId = new Map(view.overrides.map(o => [o.id, o]))
  const markers: Record<string, CellMarker> = {}

  for (const [key, ids] of Object.entries(view.overrideIdsByCell)) {
    const overrides = [...new Set(ids)].map(id => byId.get(id)).filter((o): o is SlotPlanWeekOverrideDto => !!o)
    if (overrides.length === 0) continue
    markers[key] = {
      overrides,
      kinds: kindsOf(overrides),
      description: overrides.map(describeWeekOverride).join("\n"),
    }
  }

  return { markers, legendKinds: kindsOf(view.overrides) }
}
